import type { UIMatch } from 'react-router';
import type { RouteHandle } from '@/routes/Router';

import { useNavigate, useMatches } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AccountCircleOutlined, Menu } from '@mui/icons-material';
import { Box, Button, Typography, useMediaQuery, useTheme, Tooltip } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import { theme as tgTheme } from '@theguarantors/tg-package-material-ui';

import { externalConnections, routes } from '@/constants/Constants';
import { Link } from '../../../components/Link';
import { useUrlChecker } from '../../../hooks/useUrlChecker';
import useMenu from '@/appWrapper/useMenu';
import useProfileDrawerStore from '@/stores/useProfileDrawerStore';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';

const SiteHeader = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isSigningUp, isPublic } = useUrlChecker();
  const preRegister = isPublic || window.location.pathname === routes.unitsOwnedEnroll;
  const { openMenu, menuOpen } = useMenu();
  const { showProfileDrawer } = useProfileDrawerStore();
  const { track } = useAnalyticsContext();
  const matches = useMatches() as UIMatch<unknown, RouteHandle>[];
  const currentRoute = matches[matches.length - 1];
  const hasBreadcrumbs = currentRoute?.handle?.hasBreadcrumbs === true;

  const onProfile = () => {
    track('Profile Button is clicked', {
      title: 'Profile',
      referrer: window.location.pathname,
    });
    showProfileDrawer();
  };

  const sideMenuWidth = isMobile ? 0 : menuOpen ? 240 : 72;

  const iconButtonBackgroundColors = {
    ['&:hover']: { backgroundColor: 'rgba(8, 10, 45, 0.20)' },
    ['&:active']: { backgroundColor: 'rgba(8, 10, 45, 0.12)' },
  };
  return (
    <AppBar
      sx={{
        left: `${sideMenuWidth}px !important`,
        right: '0px !important',
        margin: '0px !important',
        maxWidth: `calc(100vw - ${sideMenuWidth}px)`,
        transition: `all ${menuOpen ? 200 : 290}ms linear`,
        position: 'sticky',
        top: '0px',
        zIndex: 600,
        padding: isMobile ? '8px 12px !important' : '10px !important',
        display: 'flex',
        height: isMobile ? '64px' : '68px',
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        width: 'unset',
        boxShadow: 'none',
        borderRadius: '0px !important',
      }}
    >
      {isMobile && !preRegister ? (
        <IconButton
          aria-label="open mobile menu drawer"
          onClick={openMenu}
          sx={{
            display: { xs: 'initial', sm: 'none' },
            ...iconButtonBackgroundColors,
          }}
        >
          <Menu sx={{ width: '24px', height: '24px' }} />
        </IconButton>
      ) : null}
      {!preRegister && hasBreadcrumbs && !isMobile ? (
        <Button
          variant="text"
          startIcon={<ArrowBackIcon sx={{ width: '24px', height: '24px' }} />}
          onClick={() => navigate(-1)}
          sx={{
            color: tgTheme.palette.neutralGrey[950],
            textTransform: 'none',
            fontWeight: 400,
            fontSize: '16px',
            padding: '12px 24px',
            ...iconButtonBackgroundColors,
          }}
        >
          Back
        </Button>
      ) : null}
      {isSigningUp ? (
        <>
          <IconButton
            sx={{
              border: '1px solid black',
              mr: 2,
              ...iconButtonBackgroundColors,
            }}
            onClick={() => navigate(-1)}
            id="backButton"
          >
            <ArrowBackIcon sx={{ color: 'black', width: '24px', height: '24px' }} />
          </IconButton>
          <Typography
            onClick={() => navigate(-1)}
            sx={{ display: { xs: 'none', sm: 'flex' }, cursor: 'pointer' }}
          >
            Back
          </Typography>
        </>
      ) : null}
      <Box sx={{ flexGrow: 1 }} />
      {isSigningUp ? (
        <Link data-variant="navItem" underline="none" href={externalConnections.signin}>
          Sign In
        </Link>
      ) : null}
      {!isPublic && !isSigningUp && (
        <Tooltip title="Profile" placement="bottom">
          <IconButton
            onClick={onProfile}
            aria-label="Profile"
            sx={{
              display: 'flex',
              marginLeft: 'auto',
              width: '48px',
              height: '48px',
              borderRadius: '10px',
              ...iconButtonBackgroundColors,
            }}
          >
            <AccountCircleOutlined
              sx={{ color: tgTheme.palette.neutralGrey[500], width: '24px', height: '24px' }}
            />
          </IconButton>
        </Tooltip>
      )}
    </AppBar>
  );
};

export default SiteHeader;
